// @flow

import seedrandom from 'seedrandom';

export type Alphabet = 'alpha' | 'numeric' | 'alphanumeric' | 'base58';

export type RandomStringFunction = (
  length: number,
  alphabet?: Alphabet,
) => string;

export type RandomValueGenerator = {|
  chooseRandomItem: <T>(array: Array<T>) => T,
  generateRandom: () => number,
  generateRandomInteger: (min: number, max: number) => number,
  generateRandomString: RandomStringFunction,
|};

// Pulled from Chalk::Tools::StringUtils
const DIGITS = '0123456789'.split('');
const UPPERCASE = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const LOWERCASE = 'abcdefghijklmnopqrstuvwxyz'.split('');
const LETTERS = [...LOWERCASE, ...UPPERCASE];
const ALPHANUMERICS = [...LETTERS, ...DIGITS];
const BASE58 = [...ALPHANUMERICS].filter(
  (x) => !['0', 'O', 'I', 'l'].includes(x),
);

const getAlphabet = (alphabet: Alphabet): Array<string> => {
  switch (alphabet) {
    case 'alpha':
      return LETTERS;
    case 'numeric':
      return DIGITS;
    case 'alphanumeric':
      return ALPHANUMERICS;
    case 'base58':
      return BASE58;
    default:
      throw new Error(`Unknown random string alphabet ${alphabet}`);
  }
};

/**
 * Generates predictable psuedorandom values beginning with a specific seed.
 * We use this to generate the same "random" values on the client and the
 * server, allowing renders to remain idempotent given the same seed.
 *
 * DO NOT USE THIS FOR ANY PURPOSE WHICH MUST BE CRYPTOGRAPHICALLY SECURE!
 */
export const createRandomValueGenerator = (
  seed: string,
): RandomValueGenerator => {
  const generateRandom = seedrandom(seed);

  const generateRandomInteger = (min, max) => {
    if (min >= max) {
      throw new Error('The value for max must be greater than min');
    }
    const rand = generateRandom();
    return Math.round(min + -rand * min + rand * max);
  };

  const chooseRandomItem = <T>(array: Array<T>) => {
    if (array.length === 0) {
      throw new Error('The array cannot be empty');
    }

    return array[generateRandomInteger(0, array.length - 1)];
  };

  const generateRandomString = (length, alphabet = 'alphanumeric') => {
    if (length <= 0) {
      throw new Error('Length must be greater than zero');
    }

    const array = getAlphabet(alphabet);
    return [...Array(length)]
      .fill()
      .map(() => chooseRandomItem(array))
      .join('');
  };

  return {
    chooseRandomItem,
    generateRandom,
    generateRandomInteger,
    generateRandomString,
  };
};
